/**
 * WorkflowGraph — the program's module graph, the primary program nav (DEV-732).
 *
 * Replaces the old fixed tabs. Nodes are modules (rounded cards) laid out by
 * their ui_x/ui_y; arrows are dependency edges. Node outline colour encodes the
 * 5-state status projection. Clicking a node opens its module page.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Download, FileText, Loader2, CheckCircle2, Trash2 } from 'lucide-react'
import FileDropzone from '../../components/ui/FileDropzone'
import { tokenManager } from '../../lib/api'
import { useJobPoll } from '../../hooks/useJobPoll'
import {
  workflowsApi,
  statusOutlineClass,
  statusLabel,
  type WorkflowGraph as Graph,
  type WorkflowStepNode,
  type WorkflowOutput,
  type ContextDocument,
} from '../../services/workflows'

const API_URL = import.meta.env.VITE_API_URL || ''

/** Authed download of a workflow output (documents export behind Bearer auth). */
async function downloadOutput(o: WorkflowOutput) {
  if (!o.download_url) return
  const resp = await fetch(`${API_URL}${o.download_url}`, {
    headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` },
  })
  if (!resp.ok) return
  const blob = await resp.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${o.label || 'document'}.docx`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

const NODE_W = 240
const NODE_H = 116
const PAD = 48

function centerOf(n: WorkflowStepNode) {
  return { cx: n.ui_x + NODE_W / 2, cy: n.ui_y + NODE_H / 2 }
}

export default function WorkflowGraph() {
  const { programId } = useParams<{ programId: string }>()
  const navigate = useNavigate()
  const [graph, setGraph] = useState<Graph | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Live "AI running" indicator: one program-scoped poll marks whichever step
  // nodes currently have an active job (node.id === Job.program_step).
  const { activeJobs } = useJobPoll({ program: programId, active: true, enabled: !!programId })
  const runningStepIds = new Set(
    activeJobs.map((j) => (j.program_step == null ? null : Number(j.program_step))).filter((x) => x != null),
  )

  const load = useCallback(async () => {
    if (!programId) return
    try {
      setError(null)
      const g = await workflowsApi.getGraph(programId)
      setGraph(g)
    } catch (e: any) {
      setError(e?.message || 'Failed to load workflow')
    } finally {
      setLoading(false)
    }
  }, [programId])

  useEffect(() => { load() }, [load])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading workflow…
      </div>
    )
  }
  if (error) {
    return <div className="p-8 text-destructive">{error}</div>
  }
  if (!graph || !graph.workflow || graph.nodes.length === 0) {
    return (
      <div className="p-8 text-muted-foreground">
        No workflow is attached to this program yet.
      </div>
    )
  }

  const byKey = new Map(graph.nodes.map((n) => [n.step_key, n]))
  const width = Math.max(...graph.nodes.map((n) => n.ui_x + NODE_W)) + PAD
  const height = Math.max(...graph.nodes.map((n) => n.ui_y + NODE_H)) + PAD

  return (
    <div className="p-6">
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-foreground">{graph.workflow.name}</h1>
        <p className="text-sm text-muted-foreground">
          Click a module to work on it. Each module gathers context, runs AI, then waits for your review.
        </p>
      </div>

      <div className="relative overflow-auto rounded-lg border border-border bg-muted/20" style={{ minHeight: 320 }}>
        <div className="relative" style={{ width, height }}>
          {/* edges */}
          <svg className="absolute inset-0 pointer-events-none" width={width} height={height}>
            <defs>
              <marker id="wf-arrow" markerWidth="10" markerHeight="10" refX="8" refY="3"
                orient="auto" markerUnits="strokeWidth">
                <path d="M0,0 L8,3 L0,6 Z" className="fill-muted-foreground" />
              </marker>
            </defs>
            {graph.edges.map((e) => {
              const from = byKey.get(e.from)
              const to = byKey.get(e.to)
              if (!from || !to) return null
              const a = centerOf(from)
              const b = centerOf(to)
              // Attach to the facing edges of the node boxes.
              const x1 = from.ui_x + NODE_W
              const y1 = a.cy
              const x2 = to.ui_x
              const y2 = b.cy
              return (
                <line key={e.id} x1={x1} y1={y1} x2={x2} y2={y2}
                  className="stroke-muted-foreground/60" strokeWidth={1.5}
                  markerEnd="url(#wf-arrow)" />
              )
            })}
          </svg>

          {/* nodes */}
          {graph.nodes.map((n) => (
            <button
              key={n.id}
              onClick={() => navigate(`/programs/${programId}/module/${n.step_key}`)}
              className={`absolute text-left rounded-xl border-2 bg-card shadow-sm hover:shadow-md transition-shadow
                ${statusOutlineClass(n.status)} ${n.status === 'agent_working' ? 'animate-pulse' : ''}`}
              style={{ left: n.ui_x, top: n.ui_y, width: NODE_W, minHeight: NODE_H }}
            >
              <div className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-foreground truncate">{n.name}</span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    {runningStepIds.has(n.id) && (
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500 shrink-0" aria-label="AI running" />
                    )}
                    <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                      {statusLabel(n.status)}
                    </span>
                  </span>
                </div>
                {n.needs_attention_reason && (
                  <div className="mt-1 text-[11px] text-destructive line-clamp-2">
                    {n.needs_attention_reason}
                  </div>
                )}
                {n.outputs.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {n.outputs.map((o) => (
                      <div key={o.id} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <FileText className="w-3 h-3 shrink-0" />
                        <span className="truncate flex-1">{o.label}</span>
                        {o.download_url && (
                          // role=button (not <button>) — the node itself is a <button>.
                          <span
                            role="button"
                            tabIndex={0}
                            title={`Download ${o.label}`}
                            onClick={(e) => { e.stopPropagation(); downloadOutput(o) }}
                            className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-foreground hover:bg-accent"
                          >
                            <Download className="w-3 h-3" /> Download
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      <ContextDocuments programId={programId!} />
    </div>
  )
}

/**
 * Context Documents — upload docs for general consideration; each upload kicks
 * an async workflow-wide extraction (answers land against every workflow
 * document's questions). Shows all docs uploaded across the flow + status.
 */
function ContextDocuments({ programId }: { programId: string }) {
  const [docs, setDocs] = useState<ContextDocument[]>([])
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const load = useCallback(async () => {
    try {
      const r = await workflowsApi.listContextDocuments(programId)
      setDocs(r.documents)
    } catch { /* non-fatal */ }
  }, [programId])

  useEffect(() => { load() }, [load])

  // A chat upload toggled between "Program context" and "Chat only" (in the
  // agent drawer) adds/removes a doc here.
  useEffect(() => {
    const onChange = (e: Event) => {
      if ((e as CustomEvent).detail?.resource === 'context_document') load()
    }
    window.addEventListener('agentDataChanged', onChange)
    return () => window.removeEventListener('agentDataChanged', onChange)
  }, [load])

  // Poll while any extraction is still running.
  useEffect(() => {
    const anyRunning = docs.some((d) => d.extraction?.state === 'running')
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    if (anyRunning) pollRef.current = setInterval(load, 3000)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [docs, load])

  const onUpload = async (files: File[]) => {
    if (files.length === 0) return
    setUploading(true); setError(null)
    try {
      for (const f of files) await workflowsApi.uploadContextDocument(programId, f)
      await load()
    } catch (e: any) {
      setError(e?.message || 'Upload failed')
    } finally { setUploading(false) }
  }

  const remove = async (d: ContextDocument) => {
    const facts = d.fact_count
      ? ` and the ${d.fact_count} fact${d.fact_count === 1 ? '' : 's'} extracted from it`
      : ''
    if (!confirm(`Delete “${d.name}”${facts}? This can't be undone.`)) return
    setError(null)
    try {
      await workflowsApi.deleteContextDocument(programId, d.id)
      await load()
    } catch (e: any) {
      setError(e?.message || 'Could not delete document')
    }
  }

  return (
    <section className="mt-8">
      <h2 className="text-sm font-semibold text-foreground">Context documents</h2>
      <p className="text-xs text-muted-foreground mb-3">
        Upload background material (requirements docs, concept papers, prior packages). We'll read each
        one and extract answers to questions across every step of this workflow.
      </p>

      {error && <div className="mb-2 text-xs text-destructive">{error}</div>}

      {uploading ? (
        <div className="flex items-center text-sm text-muted-foreground mb-3">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Uploading & reading…
        </div>
      ) : (
        <div className="mb-3"><FileDropzone onFilesSelected={onUpload} /></div>
      )}

      {docs.length > 0 && (
        <ul className="space-y-1">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm">
              <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="truncate flex-1 text-foreground">{d.name}</span>
              {d.from_chat && (
                <span
                  className="shrink-0 rounded-full border border-border bg-muted px-1.5 py-px text-[10px] text-muted-foreground"
                  title="Added from the agent chat"
                >
                  from chat
                </span>
              )}
              {d.extraction?.state === 'running' && (
                <span className="inline-flex items-center text-xs text-muted-foreground">
                  <Loader2 className="w-3 h-3 animate-spin mr-1" /> Extracting…
                </span>
              )}
              {d.extraction?.state === 'completed' && (
                <span className="inline-flex items-center text-xs text-green-600">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {typeof d.extraction.answered_count === 'number'
                    ? `${d.extraction.answered_count} answers` : 'Read'}
                </span>
              )}
              {d.extraction?.state === 'failed' && (
                <span className="text-xs text-destructive">Failed</span>
              )}
              {d.download_url && (
                <a
                  href={d.download_url.startsWith('http') ? d.download_url : `${API_URL}${d.download_url}`}
                  target="_blank"
                  rel="noreferrer"
                  download
                  title={`Download ${d.name}`}
                  className="inline-flex items-center rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                  <Download className="w-4 h-4" />
                </a>
              )}
              <button
                type="button"
                onClick={() => remove(d)}
                title={`Delete ${d.name}`}
                className="inline-flex items-center rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
